// HowToToolUI — branded tool-UI for the agent's `getHowTo` (Coach) tool call,
// rendered inline in the Aidea Assistant chat thread.
//
// The backend returns a JSON string (NO images — base64 is kept out of the
// agent loop):
//   { "matched": true, "title": "...", "slug": "video-export",
//     "summary": "...", "where": "Where: ⋮ menu → Video Export",
//     "steps": [ { "n": 1, "text": "Save your training." }, ... ],
//     "guideUrl": "https://.../how-to/video-export" }
//   or { "matched": false } when no guide fits.
//
// Screenshots live in a SEPARATE channel: on mount the card fetches
//   GET <sa-base>/howto/media?slug=<slug>   (Bearer JWT)
// → { images: [ { afterStep, dataUri, caption } ] } and inserts each thumbnail
// AFTER steps[afterStep-1] (afterStep 0 = before step 1). This is best-effort:
// any fetch error / non-ok / missing token / empty images → text steps only, no
// error surfaced to the user. Display-only — no popup/gateway.
//
// The media base URL is derived from the SAME Studio-Agent base the chat uses
// (resolveAgUiUrl in ChatPanel) with the trailing "/agent" route swapped for
// "/howto/media"; the auth token comes from getToken (common/environment_config),
// exactly as ChatPanel's agent does.
//
// Styling follows the chat scoping contract: every utility is `tw-`-prefixed and
// only takes effect under `.aidea-chat-root`. Brand tokens via CSS vars. No emoji
// in JSX chrome (the `where`/guide affordances use the brand icons).

import React, { useEffect, useRef, useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, FileTextIcon, AlertIcon } from "../icons";
import { getToken } from "../../../../common/environment_config";
import { mayCarryToken } from "../../../../common/http_service";

// ---------------------------------------------------------------------------
// SA base-URL resolution — mirrors ChatPanel's resolveAgUiUrl priority order
// (?saurl= → window.AIDEA_SA_URL → window.ideaLibraryRootUrl + api/sa/agent),
// then swaps the trailing "/agent" route for "/howto/media". Derived here (not
// imported) because ChatPanel does not export it. Returns null when none of
// the sources resolve — no assumed default, see resolveMediaUrl.
// ---------------------------------------------------------------------------
function resolveSaAgentUrl() {
  if (typeof window === "undefined") return null;
  try {
    const fromParam = new URLSearchParams(window.location.search).get("saurl");
    if (fromParam) return fromParam;
  } catch {
    // URLSearchParams unavailable / malformed search — fall through.
  }
  if (typeof window.AIDEA_SA_URL === "string" && window.AIDEA_SA_URL) {
    return window.AIDEA_SA_URL;
  }
  if (typeof window.ideaLibraryRootUrl === "string" && window.ideaLibraryRootUrl) {
    return `${window.ideaLibraryRootUrl.replace(/\/+$/, "")}/api/sa/agent`;
  }
  return null;
}

// Turn the resolved agent URL into the media endpoint by replacing the final
// "/agent" path segment with "/howto/media". Falls back to appending when the
// base does not end in "/agent" (so a custom ?saurl= still routes sensibly).
// Returns null when there's no base — the caller skips the fetch entirely.
function resolveMediaUrl() {
  const base = resolveSaAgentUrl();
  if (!base) return null;
  if (/\/agent\/?$/.test(base)) {
    return base.replace(/\/agent\/?$/, "/howto/media");
  }
  return `${base.replace(/\/+$/, "")}/howto/media`;
}

// Parse the tool result into the text part `{ matched, title, slug, summary,
// where, steps, guideUrl }` (or `{ matched:false }`). The backend sends a JSON
// string; some transports may hand it back already parsed. Defensive, never throws.
function parseHowToResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (data.matched === false) return { matched: false };
  const title = typeof data.title === "string" ? data.title.trim() : "";
  const slug = typeof data.slug === "string" ? data.slug.trim() : "";
  const summary = typeof data.summary === "string" ? data.summary.trim() : "";
  const where = typeof data.where === "string" ? data.where.trim() : "";
  const guideUrl = typeof data.guideUrl === "string" ? data.guideUrl.trim() : "";
  const steps = Array.isArray(data.steps)
    ? data.steps
        .map((s, i) => ({
          n: typeof s?.n === "number" ? s.n : i + 1,
          text: typeof s?.text === "string" ? s.text : "",
        }))
        .filter((s) => s.text)
    : [];
  return { matched: true, title, slug, summary, where, steps, guideUrl };
}

// Small violet-tint header chip, e.g. "How-to".
function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <FileTextIcon size={12} />
      {label}
    </span>
  );
}

// Card chrome shared by every state so the tool always looks like one card.
function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[520px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

// Running state: spinner + "Looking up the guide…"
function RunningState() {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Looking up the guide&hellip;
        </span>
      </div>
    </Card>
  );
}

// No-match / parse-failure: a small muted line (no error chrome — Coach degrades
// quietly into a plain text reply alongside this card).
function NoMatchState() {
  return (
    <Card>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">I couldn&apos;t find a step-by-step guide for that.</span>
      </div>
    </Card>
  );
}

// Lazy-loads the guide's screenshots from the out-of-loop media endpoint and
// returns a map of afterStep → [images]. Best-effort: any failure leaves the map
// empty so only the text steps render. Guarded against unmount with isMounted.
function useGuideImages(slug) {
  const [imagesByStep, setImagesByStep] = useState({});
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (!slug) return undefined;

    const token = getToken();
    if (!token) return undefined; // No auth → text-only, silently.

    const mediaBase = resolveMediaUrl();
    if (!mediaBase) return undefined; // No SA base configured → text-only, silently.

    const url = `${mediaBase}?slug=${encodeURIComponent(slug)}`;
    if (!mayCarryToken(url)) return undefined; // ?saurl= to a foreign origin never gets the token.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res && res.ok ? res.json() : null))
      .then((data) => {
        if (!isMounted.current || !data) return;
        const images = Array.isArray(data.images) ? data.images : [];
        if (!images.length) return;
        const grouped = {};
        for (const img of images) {
          if (!img || typeof img.dataUri !== "string" || !img.dataUri.startsWith("data:")) {
            continue;
          }
          const afterStep = typeof img.afterStep === "number" ? img.afterStep : 0;
          (grouped[afterStep] = grouped[afterStep] || []).push(img);
        }
        if (Object.keys(grouped).length) setImagesByStep(grouped);
      })
      .catch(() => {
        // Best-effort: swallow any network/parse error — text steps still show.
      });

    return () => {
      isMounted.current = false;
    };
  }, [slug]);

  return imagesByStep;
}

// One screenshot thumbnail: a rounded, max-320px image that opens full-size in a
// new tab, with its caption beneath.
function Screenshot({ image }) {
  return (
    <div className="tw-mt-1.5">
      <a
        href={image.dataUri}
        target="_blank"
        rel="noopener noreferrer"
        className="tw-block tw-w-fit"
      >
        <img
          src={image.dataUri}
          alt={image.caption || "Screenshot"}
          loading="lazy"
          className="tw-max-w-[320px] tw-rounded-lg tw-border tw-border-[var(--aidea-border)]"
        />
      </a>
      {image.caption && (
        <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-italic tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
          {image.caption}
        </p>
      )}
    </div>
  );
}

// Render the screenshots that belong after a given step index (afterStep 0 =
// before step 1, afterStep N = after step N).
function ScreenshotsFor({ images }) {
  if (!images || !images.length) return null;
  return (
    <>
      {images.map((img, i) => (
        <Screenshot key={i} image={img} />
      ))}
    </>
  );
}

// Matched state: header chip + title + summary + optional `where` chip + the
// numbered steps with screenshots inserted after their step + "Open full guide".
function MatchedState({ title, summary, where, steps, guideUrl, slug }) {
  const imagesByStep = useGuideImages(slug);

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="How-to" />
      </div>

      {title && (
        <p className="tw-m-0 tw-px-3 tw-pt-1.5 tw-text-[14px] tw-font-semibold tw-leading-snug tw-text-[var(--aidea-fg)]">
          {title}
        </p>
      )}
      {summary && (
        <p className="tw-m-0 tw-px-3 tw-pt-1 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
          {summary}
        </p>
      )}

      {where && (
        <div className="tw-px-3 tw-pt-2">
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-muted-bg)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-[var(--aidea-fg)]">
            <span aria-hidden="true">📍</span>
            {where}
          </span>
        </div>
      )}

      <div className="tw-px-3 tw-py-2">
        {/* Screenshots that come BEFORE step 1 (afterStep 0). */}
        <ScreenshotsFor images={imagesByStep[0]} />
        <ol className="tw-m-0 tw-list-none tw-p-0">
          {steps.map((step) => (
            <li key={step.n} className="tw-mb-1.5 tw-last:tw-mb-0">
              <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
                <span className="tw-font-semibold tw-text-[var(--aidea-primary)]">{step.n}.</span>{" "}
                {step.text}
              </p>
              <ScreenshotsFor images={imagesByStep[step.n]} />
            </li>
          ))}
        </ol>
      </div>

      {/* Only an absolute URL is a real guide; a relative "/how-to/<slug>" (mw
          with DOCS_SITE_URL unset) resolves against the editor origin — dead link. */}
      {/^https?:\/\//i.test(guideUrl) && (
        <div className="tw-px-3 tw-pb-3">
          <a
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-medium tw-text-[var(--aidea-primary)] hover:tw-underline"
          >
            Open full guide ↗
          </a>
        </div>
      )}
    </Card>
  );
}

export const HowToToolUI = makeAssistantToolUI({
  toolName: "getHowTo",
  render: ({ result, status }) => {
    if (status && status.type === "running") {
      return <RunningState />;
    }

    const parsed = parseHowToResult(result);
    if (!parsed || parsed.matched === false) {
      return <NoMatchState />;
    }

    return (
      <MatchedState
        title={parsed.title}
        summary={parsed.summary}
        where={parsed.where}
        steps={parsed.steps}
        guideUrl={parsed.guideUrl}
        slug={parsed.slug}
      />
    );
  },
});

export default HowToToolUI;
